import type {
  ContainerStatus,
  ContainerDashboardStats,
  BatchUpgradeRequest,
  BatchUpgradeProgress,
} from '../types';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const CONTAINERS_BASE = '/api/admin/containers';
const UPGRADES_BASE = '/api/admin/upgrades';

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed (${response.status})`);
  }
  return response.json();
}

export const fetchDashboardStats = async (
  authFetch: AuthFetch
): Promise<ContainerDashboardStats> => {
  const response = await authFetch(`${CONTAINERS_BASE}/dashboard`);
  return handleResponse<ContainerDashboardStats>(response);
};

function mapStatus(backendStatus: string): ContainerStatus['status'] {
  switch (backendStatus) {
    case 'running':
      return 'running';
    case 'stopped':
      return 'stopped';
    case 'unhealthy':
    case 'error':
      return 'unhealthy';
    case 'not_provisioned':
      return 'not_provisioned';
    default:
      return 'unknown';
  }
}

function calculateUptime(startedAt: string): string {
  const start = new Date(startedAt);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h`;
  return `${Math.floor(diffMs / (1000 * 60))}m`;
}

export const fetchContainers = async (
  authFetch: AuthFetch
): Promise<ContainerStatus[]> => {
  const response = await authFetch(CONTAINERS_BASE);
  const data = await handleResponse<{ items: any[]; total: number }>(response);

  return data.items.map((item) => ({
    tenant_id: item.tenant_id,
    tenant_slug: item.tenant_slug,
    tenant_name: item.tenant_name,
    status: mapStatus(item.status),
    started_at: item.started_at,
    version: item.version || '\u2014',
    cpu_percent: 0,
    memory_mb: 0,
    uptime: item.started_at ? calculateUptime(item.started_at) : '\u2014',
  }));
};

export const startContainer = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<void> => {
  const response = await authFetch(`${CONTAINERS_BASE}/${tenantId}/start`, {
    method: 'POST',
  });
  await handleResponse<unknown>(response);
};

export const stopContainer = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<void> => {
  const response = await authFetch(`${CONTAINERS_BASE}/${tenantId}/stop`, {
    method: 'POST',
  });
  await handleResponse<unknown>(response);
};

export const restartContainer = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<void> => {
  const response = await authFetch(`${CONTAINERS_BASE}/${tenantId}/restart`, {
    method: 'POST',
  });
  await handleResponse<unknown>(response);
};

export const fetchContainerLogs = async (
  authFetch: AuthFetch,
  tenantId: string,
  service: string = 'api',
  lines: number = 100
): Promise<string> => {
  const params = new URLSearchParams({ service, lines: String(lines) });
  const response = await authFetch(
    `${CONTAINERS_BASE}/${tenantId}/logs?${params.toString()}`
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to fetch logs');
  }
  const data = await response.json();
  return data.logs ?? '';
};

export const startBatchUpgrade = async (
  authFetch: AuthFetch,
  request: BatchUpgradeRequest
): Promise<string> => {
  const response = await authFetch(`${UPGRADES_BASE}/batch/upgrade`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = await handleResponse<{ batch_id: string }>(response);
  return data.batch_id;
};

export const fetchBatchProgress = async (
  authFetch: AuthFetch,
  batchId: string
): Promise<BatchUpgradeProgress> => {
  const response = await authFetch(`${UPGRADES_BASE}/batch/${batchId}/status`);
  return handleResponse<BatchUpgradeProgress>(response);
};

export const cancelBatchUpgrade = async (
  authFetch: AuthFetch,
  batchId: string
): Promise<void> => {
  const response = await authFetch(`${UPGRADES_BASE}/batch/${batchId}/cancel`, {
    method: 'POST',
  });
  await handleResponse<unknown>(response);
};
